import { useState, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Plus,
  Edit2,
  Trash2,
  User,
  Users,
  Calendar,
  Shield,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  insertDependentSchema,
  type Dependent,
  type InsertDependent,
} from "@shared/schema";
import { DEPENDENT_RELATIONSHIP_OPTIONS } from "@shared/constants/dependentRelationships";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { formatDateDisplay } from "@/lib/utils";
import { AddressFields } from "@/components/forms/AddressFields";
import { useToast } from "@/hooks/use-toast";
import { DependentDeletionDialog } from "@/components/employee/DependentDeletionDialog";
import { useLocation } from "wouter";
import { SsnInput } from "@/components/employees/SsnInput";
import { ssnToSave } from "@/lib/ssnReveal";

interface DependentsListProps {
  employeeId: number;
  employeeName: string;
  onNavigateToEnrollments?: () => void;
}

export function DependentsList({
  employeeId,
  employeeName,
  onNavigateToEnrollments: onNavigateToEnrollmentsProp,
}: DependentsListProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingDependent, setEditingDependent] = useState<Dependent | null>(
    null
  );
  const [plansDialogOpen, setPlansDialogOpen] = useState(false);
  const [plansDialogDependent, setPlansDialogDependent] =
    useState<Dependent | null>(null);
  const [pendingDeletionDependent, setPendingDeletionDependent] = useState<Dependent | null>(null);
  const [deleteDependentError, setDeleteDependentError] = useState<string | null>(null);
  // Full SSN of the dependent being edited, once revealed (API rows carry ***-**-1234).
  const [revealedSsn, setRevealedSsn] = useState<string | null | undefined>(undefined);

  const { data: dependentsData = [], isLoading } = useQuery({
    queryKey: [`/api/employees/${employeeId}/dependents`],
    queryFn: async () => {
      const response = await fetch(`/api/employees/${employeeId}/dependents`);
      if (!response.ok) throw new Error("Failed to fetch dependents");
      return response.json();
    },
  });

  // Sort dependents: Spouse/Domestic Partner first, then Children ordered by age (youngest to oldest)
  const dependents = [...dependentsData].sort((a, b) => {
    const relA = a.relationship.toLowerCase();
    const relB = b.relationship.toLowerCase();

    // Spouse and Domestic Partner always come first
    const isPartnerA = relA === 'spouse' || relA === 'domestic_partner';
    const isPartnerB = relB === 'spouse' || relB === 'domestic_partner';

    if (isPartnerA && !isPartnerB) return -1;
    if (!isPartnerA && isPartnerB) return 1;

    // Both are partners (spouse comes before domestic partner)
    if (isPartnerA && isPartnerB) {
      if (relA === 'spouse' && relB !== 'spouse') return -1;
      if (relA !== 'spouse' && relB === 'spouse') return 1;
      return 0;
    }

    // For children and other dependents, sort by age (youngest first = most recent birth date)
    if (a.dateOfBirth && b.dateOfBirth) {
      return new Date(b.dateOfBirth).getTime() - new Date(a.dateOfBirth).getTime();
    }

    // If one has no birth date, put it after ones with birth dates
    if (a.dateOfBirth && !b.dateOfBirth) return -1;
    if (!a.dateOfBirth && b.dateOfBirth) return 1;

    // Both have no birth date, maintain original order
    return 0;
  });

  const { data: dependentEnrollmentIds = [] } = useQuery({
    queryKey: ["/api/employees", employeeId, "dependent-enrollment-ids"],
    queryFn: async () => {
      const res = await fetch(
        `/api/employees/${employeeId}/dependent-enrollments`
      );
      if (!res.ok) throw new Error("Failed to fetch dependent enrollments");
      const json = await res.json();
      return Array.isArray(json?.dependentIds) ? json.dependentIds : [];
    },
  });

  const { data: employeeBenefits = [] } = useQuery({
    queryKey: ["/api/employees", employeeId, "benefits-summary"],
    enabled: plansDialogOpen,
    queryFn: async () => {
      const res = await fetch(`/api/employees/${employeeId}/benefits`);
      if (!res.ok) throw new Error("Failed to fetch benefits");
      return res.json();
    },
  });

  const form = useForm<InsertDependent>({
    resolver: zodResolver(insertDependentSchema),
    defaultValues: {
      employeeId,
      firstName: "",
      middleInitial: "",
      lastName: "",
      relationship: "",
      dateOfBirth: undefined,
      sex: "",
      ssn: "",
      ssnNotAvailable: false,
      ssnFollowUpDate: undefined,
      hasDifferentAddress: false,
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipCode: "",
      county: "",
      country: "United States",
      isEligible: true,
      isDisabled: false,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: InsertDependent) => {
      // Calculate follow-up date if SSN not available (default 2 weeks from now)
      let processedData = { ...data };
      if (data.ssnNotAvailable && !data.ssnFollowUpDate) {
        const followUpDate = new Date();
        followUpDate.setDate(followUpDate.getDate() + 14); // 2 weeks
        processedData.ssnFollowUpDate = followUpDate.toISOString().split('T')[0];
      }

      const response = await fetch("/api/dependents", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(processedData),
      });
      if (!response.ok) throw new Error("Failed to create dependent");
      const newDependent = await response.json();

      // Create a ticket if SSN is not available
      if (processedData.ssnNotAvailable && processedData.ssnFollowUpDate) {
        try {
          // Generate a simple ticket number (in production, this should be more robust)
          const ticketNumber = `SSN-${Date.now()}-${newDependent.id}`;

          await fetch("/api/tasks", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ticketNumber,
              title: `Follow-up Required: SSN for ${data.firstName} ${data.lastName}`,
              description: `SSN is currently unavailable for dependent ${data.firstName} ${data.lastName}. This information is required for benefits enrollment. Please follow up to obtain the SSN by ${processedData.ssnFollowUpDate}.`,
              priority: "high",
              status: "open",
              category: "benefits_enrollment",
              subcategory: "missing_information",
              requesterEmail: "system@example.com", // System-generated
              requesterName: "System",
              requesterType: "system",
              employeeId,
              dependentId: newDependent.id,
              estimatedResolutionDate: processedData.ssnFollowUpDate,
            }),
          });
        } catch (error) {
          console.error("Failed to create follow-up ticket:", error);
        }
      }

      return newDependent;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      setIsDialogOpen(false);
      form.reset();
      toast({
        variant: "success",
        title: "Dependent added successfully",
        description: `${data.firstName} ${data.lastName} has been added as a dependent.`,
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<InsertDependent>;
    }) => {
      const response = await fetch(`/api/dependents/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error("Failed to update dependent");
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      setIsDialogOpen(false);
      setEditingDependent(null);
      form.reset();
      toast({
        variant: "success",
        title: "Dependent updated successfully",
        description: `${data.firstName} ${data.lastName} has been updated.`,
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest("DELETE", `/api/dependents/${id}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [`/api/employees/${employeeId}/dependents`],
      });
      const dependentLabel = pendingDeletionDependent
        ? `${pendingDeletionDependent.firstName} ${pendingDeletionDependent.lastName}`.trim() || "Dependent"
        : "Dependent";
      toast({
        title: "Dependent Deleted",
        description: `${dependentLabel} has been removed successfully.`,
        variant: "success",
      });
      setPendingDeletionDependent(null);
      setDeleteDependentError(null);
    },
    onError: (error: any) => {
      const message = error?.message || "Failed to delete dependent. Please try again.";
      setDeleteDependentError(message);
      toast({
        title: "Unable to delete dependent",
        description: message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InsertDependent) => {
    if (editingDependent) {
      // Unchanged (masked or same as revealed) SSN is not sent, so it can't overwrite the stored one.
      const payload: Partial<InsertDependent> = { ...data };
      if (ssnToSave(payload.ssn, revealedSsn) === undefined) delete payload.ssn;
      updateMutation.mutate({ id: editingDependent.id, data: payload });
    } else {
      createMutation.mutate(data);
    }
  };

  const handleEdit = (dependent: Dependent) => {
    setEditingDependent(dependent);
    setRevealedSsn(undefined);
    form.reset({
      employeeId: dependent.employeeId,
      firstName: dependent.firstName,
      middleInitial: (dependent as any).middleInitial || "",
      lastName: dependent.lastName,
      relationship: dependent.relationship,
      dateOfBirth: dependent.dateOfBirth
        ? new Date(dependent.dateOfBirth)
        : undefined,
      sex: dependent.sex || "",
      ssn: dependent.ssn || "",
      ssnNotAvailable: (dependent as any).ssnNotAvailable || false,
      ssnFollowUpDate: (dependent as any).ssnFollowUpDate || undefined,
      hasDifferentAddress: (dependent as any).hasDifferentAddress || false,
      address1: (dependent as any).address1 || "",
      address2: (dependent as any).address2 || "",
      city: (dependent as any).city || "",
      state: (dependent as any).state || "",
      zipCode: (dependent as any).zipCode || "",
      county: (dependent as any).county || "",
      country: (dependent as any).country || "United States",
      isEligible: dependent.isEligible || false,
      isDisabled: (dependent as any).isDisabled || false,
    });
    setIsDialogOpen(true);
  };

  const handleRequestDeleteDependent = useCallback((dependent: Dependent) => {
    setDeleteDependentError(null);
    setPendingDeletionDependent(dependent);
  }, []);

  const handleCloseDependentDeletion = useCallback(() => {
    setPendingDeletionDependent(null);
    setDeleteDependentError(null);
  }, []);

  const handleConfirmDependentDeletion = useCallback(async () => {
    if (!pendingDeletionDependent?.id) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(pendingDeletionDependent.id as number);
    } catch (error) {
      // Error is handled by onError handler
      throw error;
    }
  }, [pendingDeletionDependent?.id, deleteMutation]);

  const handleNavigateToEnrollments = useCallback(() => {
    setPendingDeletionDependent(null);
    if (onNavigateToEnrollmentsProp) {
      onNavigateToEnrollmentsProp();
    }
  }, [onNavigateToEnrollmentsProp]);


  const getRelationshipIcon = (relationship: string) => {
    switch (relationship.toLowerCase()) {
      case "spouse":
      case "domestic_partner":
        return <User className="h-4 w-4" />;
      case "child":
        return <Calendar className="h-4 w-4" />;
      default:
        return <User className="h-4 w-4" />;
    }
  };

  const formatRelationship = (relationship: string) => {
    if (relationship.toLowerCase() === 'domestic_partner') {
      return 'Domestic Partner';
    }
    return relationship.charAt(0).toUpperCase() + relationship.slice(1).toLowerCase();
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Dependents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32">
            <div className="text-muted-foreground">Loading dependents...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Dependents ({dependents.length})
        </CardTitle>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <PermissionButton
              permission="clients:edit"
              whenDenied="hide"
              size="sm"
              onClick={() => {
                setEditingDependent(null);
                form.reset({
                  employeeId,
                  firstName: "",
                  middleInitial: "",
                  lastName: "",
                  relationship: "",
                  dateOfBirth: undefined,
                  sex: "",
                  ssn: "",
                  ssnNotAvailable: false,
                  ssnFollowUpDate: undefined,
                  hasDifferentAddress: false,
                  address1: "",
                  address2: "",
                  city: "",
                  state: "",
                  zipCode: "",
                  county: "",
                  country: "United States",
                  isEligible: true,
                  isDisabled: false,
                });
              }}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Dependent
            </PermissionButton>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg lg:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingDependent ? "Edit Dependent" : "Add New Dependent"}
              </DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form
                onSubmit={form.handleSubmit(onSubmit)}
                className="space-y-4"
              >
                <div className="grid grid-cols-[1fr_5rem_1fr] gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="middleInitial"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>MI</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            value={field.value ?? ""}
                            maxLength={5}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="relationship"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Relationship</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select relationship" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {DEPENDENT_RELATIONSHIP_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="sex"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sex</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select sex" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="male">Male</SelectItem>
                          <SelectItem value="female">Female</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="dateOfBirth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Date of Birth</FormLabel>
                      <FormControl>
                        <Input
                          type="date"
                          {...field}
                          value={
                            field.value
                              ? new Date(field.value)
                                  .toISOString()
                                  .split("T")[0]
                              : ""
                          }
                          onChange={(e) =>
                            field.onChange(
                              e.target.value
                                ? new Date(e.target.value)
                                : undefined
                            )
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ssn"
                  render={({ field }) => {
                    const ssnNotAvailable = form.watch("ssnNotAvailable");
                    return (
                      <FormItem>
                        <FormLabel>SSN {!ssnNotAvailable && <span className="text-red-500">*</span>}</FormLabel>
                        <FormControl>
                          <SsnInput
                            employeeId={employeeId}
                            dependentId={editingDependent?.id}
                            value={field.value || ""}
                            onChange={field.onChange}
                            onRevealed={setRevealedSsn}
                            disabled={!!ssnNotAvailable}
                            required={!ssnNotAvailable}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
                </div>

                <FormField
                  control={form.control}
                  name="ssnNotAvailable"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            // Clear SSN when checked
                            if (checked) {
                              form.setValue("ssn", "");
                            }
                          }}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel className="flex items-center gap-2">
                          SSN not currently available
                          {field.value && (
                            <Badge variant="outline" className="ml-1">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Follow-up required
                            </Badge>
                          )}
                        </FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Check this to bypass SSN requirement and create a follow-up task
                        </p>
                      </div>
                    </FormItem>
                  )}
                />

                {form.watch("ssnNotAvailable") && (
                  <FormField
                    control={form.control}
                    name="ssnFollowUpDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Follow-up Date</FormLabel>
                        <FormControl>
                          <Input
                            type="date"
                            {...field}
                            value={
                              field.value
                                ? typeof field.value === 'string'
                                  ? field.value
                                  : new Date(field.value).toISOString().split("T")[0]
                                : ""
                            }
                            onChange={(e) => field.onChange(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                          />
                        </FormControl>
                        <p className="text-xs text-muted-foreground">
                          Default is 2 weeks from today. A task will be created to follow up.
                        </p>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <FormField
                  control={form.control}
                  name="hasDifferentAddress"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                      <FormControl>
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>Different address than employee?</FormLabel>
                        <p className="text-sm text-muted-foreground">
                          Check if this dependent has a different address (e.g., separated parents)
                        </p>
                      </div>
                    </FormItem>
                  )}
                />

                {form.watch("hasDifferentAddress") && (
                  <div className="space-y-4 pt-2">
                    <div className="text-sm font-medium">Dependent Address</div>
                    <AddressFields control={form.control} />
                  </div>
                )}

                <FormField
                  control={form.control}
                  name="isEligible"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                      <div className="space-y-0.5">
                        <FormLabel>Coverage Eligible</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="isDisabled"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 shadow-sm">
                      <div className="space-y-0.5">
                        <FormLabel>Is Disabled</FormLabel>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value ?? false}
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                {/* Removed enrolledInPlan toggle; enrollment shown via dependent_enrollments */}

                <div className="flex justify-end space-x-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsDialogOpen(false);
                      setEditingDependent(null);
                      form.reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createMutation.isPending || updateMutation.isPending
                    }
                  >
                    {editingDependent ? "Update" : "Add"} Dependent
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {dependents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">No dependents found</p>
            <p className="text-sm">
              Add family members and beneficiaries for {employeeName}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {dependents.map((dependent: Dependent) => (
              <div
                key={dependent.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center space-x-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                    {getRelationshipIcon(dependent.relationship)}
                  </div>
                  <div>
                    <div className="font-medium">
                      {dependent.firstName} {dependent.lastName}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span>
                        {formatRelationship(dependent.relationship)}
                      </span>
                      {dependent.dateOfBirth && (
                        <>
                          <span>•</span>
                          <span>Born {formatDateDisplay(dependent.dateOfBirth) || "N/A"}</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    {dependent.isEligible ? (
                      <Badge
                        variant="secondary"
                        className="flex items-center gap-1"
                      >
                        <Shield className="h-3 w-3" />
                        Eligible
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        <Shield className="h-3 w-3" />
                        Not Eligible
                      </Badge>
                    )}
                    {dependentEnrollmentIds.includes(dependent.id) ? (
                      <Badge
                        variant="default"
                        className="flex items-center gap-1 cursor-pointer"
                        onClick={() => {
                          setPlansDialogDependent(dependent);
                          setPlansDialogOpen(true);
                        }}
                      >
                        <CheckCircle className="h-3 w-3" />
                        Enrolled
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="flex items-center gap-1"
                      >
                        <XCircle className="h-3 w-3" />
                        Not Enrolled
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-1">
                    <PermissionButton
                      permission="clients:edit"
                      whenDenied="hide"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEdit(dependent)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </PermissionButton>
                    <PermissionButton
                      permission="clients:edit"
                      whenDenied="hide"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleRequestDeleteDependent(dependent)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </PermissionButton>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
      {/* Dependent Plans Dialog */}
      <Dialog open={plansDialogOpen} onOpenChange={setPlansDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {plansDialogDependent
                ? `${plansDialogDependent.firstName} ${plansDialogDependent.lastName} — Enrolled Plans`
                : "Enrolled Plans"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {!plansDialogDependent ? (
              <div className="text-sm text-muted-foreground">
                No dependent selected.
              </div>
            ) : (
              <div className="space-y-2">
                {(() => {
                  const depId = plansDialogDependent.id;
                  const rows = (employeeBenefits as any[]).filter(
                    (e: any) =>
                      Number.isFinite(e.dependentId) && e.dependentId === depId
                  );
                  if (rows.length === 0) {
                    return (
                      <div className="text-sm text-muted-foreground">
                        No active plan enrollments.
                      </div>
                    );
                  }
                  return (
                    <ul className="text-sm list-disc list-inside">
                      {rows.map((r: any) => (
                        <li key={r.id}>
                          {r?.plan?.name
                            ? `${r.plan.name} (${r.plan.type})`
                            : `Plan #${r.planId}`}{" "}
                          • Effective{" "}
                          {r.effectiveDate
                            ? formatDateDisplay(r.effectiveDate)
                            : "-"}
                        </li>
                      ))}
                    </ul>
                  );
                })()}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      <DependentDeletionDialog
        dependent={pendingDeletionDependent ? {
          id: pendingDeletionDependent.id!,
          firstName: pendingDeletionDependent.firstName,
          lastName: pendingDeletionDependent.lastName,
          relationship: pendingDeletionDependent.relationship,
        } : null}
        isOpen={Boolean(pendingDeletionDependent)}
        onClose={handleCloseDependentDeletion}
        onConfirm={handleConfirmDependentDeletion}
        onNavigateToEnrollments={handleNavigateToEnrollments}
        isProcessing={deleteMutation.isPending}
        errorMessage={deleteDependentError}
      />
    </Card>
  );
}